import React from 'react';
import {
  Box,
  Card,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Pagination,
  TextField,
  InputAdornment,
  Typography,
  Switch,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import { useDispatch, useSelector } from 'react-redux';
import {
  storeDeleteShiftTemplate,
  storeRestoreShiftTemplate,
  storeGetAllShiftTemplatesByPaginate,
  storeGetShiftTemplateById,
} from '../../../store/shift_templates';
import { EditPlantillaTurnoModal } from './modals/EditPlantillaTurnoModal';
import { ModalConfirmDeactivation } from '../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../components/modals/ModalConfirmRestore';
import { useTableState } from '../../../hooks/useTableState';
import { hasPemissions } from '../../../commons/utils';

// import { HeadCell, StyledTableRow } from '../../../components/tables/TableCustoms';
import { GenericTableHead, HeadCell } from '../../../commons/generics/GenericTableHead';
import { SinDatos } from '../../../commons/generics/SinDatos';
import { ClampedText } from '../../../commons/generics/ClampedText';
import { StyledTableRow, StyledTableCell } from '../../../commons/generics/StyledTableComponents';
import {
  usarFuncionesComposicion,
  corregirTexto,
  firstLetterUpperCase,
} from '../../../commons/utils/helpers';

const headCells: HeadCell[] = [
  { id: 'name', label: 'Nombre' },
  { id: 'position', label: 'Cargo' },
  { id: 'startTime', label: 'Hora Inicio', align: 'center' },
  { id: 'endTime', label: 'Hora Fin', align: 'center' },
  { id: 'durationHours', label: 'Duración (Hrs)', align: 'center' },
  { id: 'actions', label: 'Acciones', align: 'center' },
];

export const TablePlantillaTurnos = () => {
  const dispatch = useDispatch<any>();
  const [isViewMode, setIsViewMode] = React.useState(false);
  const [openModalForm, setOpenModalForm] = React.useState(false);

  const tableState = useTableState('shiftTemplates');
  const {
    dataTableList,
    canView,
    setCanView,
    canEdit,
    setCanEdit,
    canDelete,
    setCanDelete,
    canRestore,
    setCanRestore,
    searchTerm,
    checked,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    handleSwitchChange,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    getSortedAndFilteredList,
    isServerSide,
    order,
    orderBy,
    handleRequestSort,
    openElem,
    setOpenElem,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    handleResponse,
    ResponseComponents,
    updatePaginationFromList,
  } = tableState;

  const { list: shiftTemplates }: any = useSelector((state: any) => state.shiftTemplates);

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'view',
      'modulo.programador.plantillas'
    );
    if (view) setCanView(true);

    const edit = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'edit',
      'modulo.programador.plantillas'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'delete',
      'modulo.programador.plantillas'
    );
    if (del) setCanDelete(true);

    const restore = await hasPemissions(
      'modulo.thumano',
      'modulo.programador',
      'restore',
      'modulo.programador.plantillas'
    );
    if (restore) setCanRestore(true);
  };

  React.useEffect(() => {
    checkPermissions();
  }, []);

  React.useEffect(() => {
    if (canView) {
      dispatch(storeGetAllShiftTemplatesByPaginate(1));
    }
  }, [dispatch, canView]);

  React.useEffect(() => {
    if (shiftTemplates) {
      updatePaginationFromList(shiftTemplates);
    }
  }, [shiftTemplates]);

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    changePaginationPageHook(event, value);
    const response = await dispatch(
      storeGetAllShiftTemplatesByPaginate(value, searchTerm, checked ? 0 : 1)
    );
    await handleResponse(response, 'listar plantillas', false);
  };

  const reloadTable = () => {
    dispatch(storeGetAllShiftTemplatesByPaginate(pagePagination, searchTerm, checked ? 0 : 1));
  };

  const getInitialData = (currentStatus?: number) => {
    const status = currentStatus !== undefined ? currentStatus : checked ? 0 : 1;
    dispatch(storeGetAllShiftTemplatesByPaginate(1, '', status));
  };

  const handleSearch = (param: string) => {
    handleSearchHook(
      param,
      dispatch,
      (p: string) => storeGetAllShiftTemplatesByPaginate(1, p, checked ? 0 : 1),
      () => getInitialData(checked ? 0 : 1),
      null,
      'buscar plantilla'
    );
  };

  const handleResetSearch = () => {
    handleResetSearchHook(() => getInitialData(checked ? 0 : 1));
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;

    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => storeGetAllShiftTemplatesByPaginate(1, p, status),
        () => getInitialData(status),
        null,
        'buscar plantilla'
      );
    } else {
      getInitialData(status);
    }
  };

  const handleView = async (row: any) => {
    const response = await dispatch(storeGetShiftTemplateById(row.id));
    await handleResponse(response, 'obtener plantilla', false);
    if (response?.ok) {
      setIsViewMode(true);
      setOpenElem(row);
      setOpenModalForm(true);
    }
  };

  const handleEdit = async (row: any) => {
    const response = await dispatch(storeGetShiftTemplateById(row.id));
    await handleResponse(response, 'obtener plantilla', false);
    if (response?.ok) {
      setIsViewMode(false);
      setOpenElem(row);
      setOpenModalForm(true);
    }
  };

  const handleDelete = (row: any) => {
    setOpenElem(row);
    setOpenModalDeactivation(true);
  };

  const handleRestore = (row: any) => {
    setOpenElem(row);
    setOpenModalRestore(true);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeDeleteShiftTemplate(openElem.id, searchTerm, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'desactivar plantilla de turnos', true);
    if (response?.ok) setOpenElem(null);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      storeRestoreShiftTemplate(openElem.id, searchTerm, pagePagination, checked ? 0 : 1)
    );
    await handleResponse(response, 'restaurar plantilla de turnos', true);
    if (response?.ok) setOpenElem(null);
  };

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['name', 'startTime', 'endTime'],
    'status',
    checked ? 0 : 1
  );

  return (
    <Card sx={{ borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', p: 2 }}
      >
        <TextField
          placeholder="Buscar plantilla..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivas
          </Typography>
        </Box>
      </Box>

      <TableContainer>
        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="medium">
          <GenericTableHead
            headCells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {sortedList.length > 0 ? (
              sortedList
                .slice(
                  isServerSide ? 0 : page * rowsPerPage,
                  isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                )
                .map((row: any) => {
                  return (
                    <StyledTableRow hover key={row.id}>
                      <StyledTableCell>
                        <ClampedText
                          text={usarFuncionesComposicion(
                            corregirTexto,
                            firstLetterUpperCase,
                            row.name
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <ClampedText
                          text={usarFuncionesComposicion(
                            corregirTexto,
                            firstLetterUpperCase,
                            row.position?.label || '--'
                          )}
                        />
                      </StyledTableCell>
                      <StyledTableCell align="center">{row.startTime}</StyledTableCell>
                      <StyledTableCell align="center">{row.endTime}</StyledTableCell>
                      <StyledTableCell align="center">{row.durationHours}</StyledTableCell>

                      <StyledTableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                            <IconButton
                              size="small"
                              onClick={() => canView && handleView(row)}
                              disabled={!canView}
                              color="info"
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          <span
                            style={{
                              cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => canEdit && row.status === 1 && handleEdit(row)}
                              disabled={!canEdit || row.status !== 1}
                              color="primary"
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>

                          {row.status === 1 ? (
                            <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canDelete && handleDelete(row)}
                                disabled={!canDelete}
                                color="error"
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          ) : (
                            <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canRestore && handleRestore(row)}
                                disabled={!canRestore}
                                color="success"
                              >
                                <RestoreOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          )}
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={5} align="center">
                  <SinDatos />
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {sortedList && sortedList.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={sortedList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
        />
      )}

      {dataTableList && isServerSide && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 1,
            padding: 2.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Pagination
            sx={{ justifyContent: 'center' }}
            color="primary"
            count={itemCountPagination}
            page={pagePagination}
            onChange={changePaginationPage}
            showFirstButton
            showLastButton
            shape="circular"
          />
        </Box>
      )}

      {openModalForm && openElem && (
        <EditPlantillaTurnoModal
          open={openModalForm}
          setOpen={(val: boolean) => {
            setOpenModalForm(val);
            if (!val) setOpenElem(null);
          }}
          reloadTable={reloadTable}
          item={openElem}
          isViewMode={isViewMode}
        />
      )}

      {ResponseComponents}

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        onConfirm={confirmDeactivate}
      />

      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        onConfirm={confirmRestore}
      />
    </Card>
  );
};
